import {
  AfterContentInit,
  AfterViewInit,
  Component,
  HostListener,
  Input,
  OnInit,
} from "@angular/core";
import { CodigoBarrasUtil } from "../../util/codigo-barras-util";
import { AmfUtil } from "../../util/amf-util";
import { CodigoBarrasService } from "../../service/produto/codigo-barras.service";
import { AlertService } from "../../service/shared/alert.service";
export interface Handler {
  type: string;
  formatter: (value: string | number | Date) => string;
}

export interface HandlerOnClick extends Handler{
  handlerOnClick: (event: HandlerOnClick) => boolean;
}

export interface InputHandler extends Handler {
  handler: (event: KeyboardEvent) => boolean;
  validator: (value: string) => boolean;
  converter: (value: string) => string | number | Date;
}

function isInputHandler(object: any): boolean {
  return "handler" in object && "converter" in object;
}

function isOnClickHandler(object: any): boolean {
  return "handlerOnClick" in object && !("converter" in object);
}

export interface SgGridConfig {
  grows: number[];
  headers: string[];
  fields: string[];
  handlers: Handler[];
}

export class SgGridBuilder {
  private config: SgGridConfig = {
    grows: [],
    headers: [],
    fields: [],
    handlers: [],
  };

  public column(
    header: string,
    field: string,
    handler: Handler,
    grow: number
  ): SgGridBuilder {
    this.config.headers.push(header);
    this.config.fields.push(field);
    this.config.handlers.push(handler);
    this.config.grows.push(grow);
    return this;
  }

  public build(): SgGridConfig {
    return this.config;
  }
}

class Cell {
  constructor(
    public value: any, 
    public isEditable: boolean,
    public isClickable?: boolean,
    public isSelected?: boolean) {}
}

@Component({
  selector: "sg-grid",
  templateUrl: "./sg-grid.component.html",
  styleUrls: ["./sg-grid.component.css"]
})
export class SgGridComponent implements OnInit, AfterContentInit, AfterViewInit {
  @Input() columns: SgGridConfig;
  @Input() rows: any[];
  @Input() cotacao: any;
  @Input() confirmEdit: (row: number, item: any, field: string, value: string) => boolean;
  
  @Input() height: string;

  searchText: string = '';
  selectedRowsIndexes: number[] = [];
  selectedRowIndex:number = 0;

  cols: string;
  widths: number[];
  lefts: number[];
  tops: number[];
  cells: Cell[];
  columnCount: number;

  private totalGrows: number;
  private editings: boolean[] = [];
  private originalValues: string[] = [];

  private readonly SELECTED = "selected";
  private readonly EDITING = "editing";
  private readonly EDITING_ROW = "editingRow";
  private readonly EDITABLE = "editable";
  private readonly RIGHT_ALIGNED = "rightAligned";

  private gridRows: HTMLDivElement;
  private rowsContainer: HTMLDivElement;

  private rowHeight: number;
  private visibleRowsCount: number;
  private isRowCreated: boolean[] = [];
  private rowStartScroll: number = 0;

  private isScrolling = null;
  public waySort = "asc";
  public fieldSortable: any;

  constructor(private _codigoBarrasService: CodigoBarrasService,
              private _alertService: AlertService){

  }

  ngOnInit() {
    this.gridRows = document.getElementById("grid-rows") as HTMLDivElement;
    this.rowsContainer = document.getElementById("rows-container") as HTMLDivElement;
    this.totalGrows = 0;
    this.columns.grows.forEach(grow => (this.totalGrows += grow));
    this.widths = [];
    this.loadCells();
    this.columnCount = this.columns.headers.length;
  }

  loadCells() {
    let editables = new Array<boolean>();
    let labelOnClicks = new Array<boolean>();
    for (let index = 0; index < this.columns.fields.length; index++){
      editables[this.columns.fields[index]] = isInputHandler(this.columns.handlers[index]);
      labelOnClicks[this.columns.fields[index]] = isOnClickHandler(this.columns.handlers[index]); 
    }
    document.getElementById("rows-container").innerHTML = "";
    this.cells = [];
    this.rows.forEach((row, index) => {
      this.isRowCreated[index] = false;
      this.columns.fields.forEach(field =>
        this.cells.push(new Cell(getValue(row, field), !!editables[field], !!labelOnClicks[field]))
      )
    });
  }

  ngAfterContentInit(): void {
    this.updateGrows();
  }

  onSort(index){
    this.waySort = (this.waySort == "asc") ? "desc" : "asc";
    this.fieldSortable = index;
    let prop = this.columns.fields[index];
    const typeData = this.inputType(index);
    if(this.waySort == "asc"){
      if(typeData == "string"){
        this.rows = this.rows.sort((a: any, b: any) => {
          return a[prop].localeCompare(b[prop]);
        })
      }else{
        this.rows =  this.rows.sort((a: any, b: any) => {
          return a[prop]-b[prop];
        })
      }
    }else{
      if(typeData == "string"){
        this.rows = this.rows.sort((a: any, b: any) => {
          return b[prop].localeCompare(a[prop]);
        })
      }else{
        this.rows = this.rows.sort((a: any, b: any) => {
          return b[prop]- a[prop];
        })
      }
    }
    this.loadCells();
    this.loadRows();
  }

  ngAfterViewInit(): void {
    this.rowHeight = this.calculateRowHeight();
    this.rowsContainer.style.height = this.rowsCount() * this.rowHeight + "px";
    this.visibleRowsCount = this.getVisibleRowsCount();
    for (let row = 0; row < this.visibleRowsCount; row++)
      this.createRow(row);
    this.rowStartScroll = this.getFirstVisibleRow();
  }

  private rowsCount(): number {
    return this.rows.length;
  }

  @HostListener("window:resize")
  updateGrows(): void {
    if (this.gridRows) {
      let weight = this.gridRows.clientWidth / this.totalGrows;
      let sum = 0;
      for (let col = 0; col < this.columnCount - 1; col++) {
        this.widths[col] = Math.floor(weight * this.columns.grows[col]);
        sum += this.widths[col];
      }
      this.widths[this.columnCount - 1] = this.gridRows.clientWidth - sum;
      this.cols = "";
      this.widths.forEach(width => this.cols += (width + "px "))
      this.updateCellSizesAndPositions();
    }
  }

  private getVisibleRowsCount(): number {
    if (this.gridRows && this.gridRows.clientHeight < screen.height) 
      return Math.ceil(this.gridRows.clientHeight / this.rowHeight);
    else 
      return 0;
  }

  searchCells(event: any) {
    this.loadCells();
    (event.key === 'Enter') ? this.updateSelectedRow() : this.search();
    this.loadRows();  
  }

  updateSelectedRow() {
    // reset the index of the selected rows indexes array
    (this.selectedRowIndex < this.selectedRowsIndexes.length - 1) ? this.selectedRowIndex++ : this.selectedRowIndex = 0;
    // sets the offset of table
    this.gridRows.scrollTop = this.selectedRowsIndexes[this.selectedRowIndex] * this.rowHeight;
    //applies the SELECTED class to the current row in the selected row indexes
    if (this.selectedRowsIndexes.length > 0) this.getColumnsOfRow(this.selectedRowsIndexes[this.selectedRowIndex]).forEach((index) => {
      this.cells[index].isSelected = true;
    });
  }

  search() {
    // removes the SELECTED class if applied previously
    this.cells.forEach(cell => cell.isSelected = false);
    // only search if string is not empty
    if (this.searchText !== '') {
      // resets the selected row indexes array
      this.selectedRowsIndexes = [];
      this.selectedRowIndex = 0;
      // populates the selected row indexes array with row indexes that include string searched
      let index: number = 0;
      for (const row of this.rows) {
        for (const column of this.columns.fields) {
          if (String(getValue(row,column)).toUpperCase().indexOf(this.searchText.toUpperCase()) !== -1) {
            this.selectedRowsIndexes.push(index);
            break;
          }
        }
        index++;
      }
      // sets the offset of table
      this.gridRows.scrollTop = this.selectedRowsIndexes[this.selectedRowIndex] * this.rowHeight;
      //applies the SELECTED class to the current row in the selected row indexes
      if (this.selectedRowsIndexes.length > 0) this.getColumnsOfRow(this.selectedRowsIndexes[this.selectedRowIndex]).forEach((index) => {
        this.cells[index].isSelected = true;
      });
    }
  }

  isEvenLine(index: number) {
    return this.getRow(index) % 2 == 0;
  }

  onClick(index: number) {
    if (!this.isEditing(index)) 
      this.startEdit(index);
  }

  async onClickCodigosBarras(index: number) {

      let row = this.rows.find((row)=> row.codBarra == this.cells[index].value);

      let res = await this._codigoBarrasService.findByClienteAndCnpjAndProduto(this.cotacao.mercado.clienteSgs, row.cnpjClienteSG, row.codigoPro);
      if (AmfUtil.validarResponse(res)) {
          let codigosBarras = res.data.body.map(r => Object.assign(CodigoBarrasUtil.getInstance(), r));
          codigosBarras.push(row);
          if (codigosBarras.length){
              this._alertService.message('Códigos de barras', codigosBarras.map(c =>  "\n" + c.codBarra));
          }
      }
  }

  onKeyDown(fromIndex: number, event: KeyboardEvent): boolean {
    let isMoving = false;
    let toIndex = -1;
    switch (event.key) {
      case "ArrowUp" :
        toIndex = fromIndex - this.columnCount;
        isMoving = true;
        break;
      case "ArrowDown" :
      case "Enter":
        toIndex = fromIndex + this.columnCount;
        isMoving = true;
        break;
      case "Tab" :
        toIndex = event.shiftKey 
          ? this.previousEditableIndex(fromIndex) 
          : this.nextEditableIndex(fromIndex);
        isMoving = true;
        break;
      case "Escape" :
        this.cancelEdit(fromIndex);
        return false;
    }  

    if (isMoving) {
      event.preventDefault();
      let confirmed = !this.isEditing(fromIndex) || this.saveOrCancelEdit(fromIndex);
      if (confirmed)
        this.startEdit(toIndex);
      return false;
    } else {
      let handler = this.columns.handlers[this.getColumn(fromIndex)] as InputHandler;
      let result = handler.handler(event);
      if (!result)
        event.preventDefault();
      return result;
    }
  }

  saveOrCancelEdit(index: number): boolean {
    if (this.isEditing(index)) {
      if (this.hasChanged(index)) {
        if (this.confirm(index)) {
          this.saveEdit(index);
        } else {
          let inp = this.input(index);
          inp.focus();
          inp.select();
          return false;
        }
      } else 
        this.cancelEdit(index);
    }
    return true;
  }

  startEdit(index: number) {
    if (index > -1 && index < this.cells.length) {
      let row = this.getRow(index);
      if (!this.isRowCreated[row])
        this.createRow(row);
      this.setEditing(index, true);
      this.forCellElements(index, (div, span, input) => {
        div.setAttribute("class", this.getCellClasses(index));
        span.hidden = true;
        input.value = this.getFormattedValue(index);
        this.originalValues[index] = input.value;        
        input.setAttribute("type", "text");
        input.focus();
        input.select();
      });
      this.setRowEditingClass(index);
    }
  }

  cancelEdit(index: number) {
    this.unsetRowEditingClass(index);
    this.setEditing(index, false);
    this.forCellElements(index, (div, span, input) => {
      div.setAttribute("class", this.getCellClasses(index));
      span.hidden = false;
      input.setAttribute("type", "hidden");
      input.value = this.cells[index].value;
    });
  }

  saveEdit(index: number) {
    this.unsetRowEditingClass(index);
    this.setEditing(index, false);
    this.forCellElements(index, (div, span, input) => {
      this.cells[index].value = this.inputHandler(index).converter(input.value);
      div.setAttribute("class", this.getCellClasses(index));
      span.hidden = false;
      span.textContent = this.getFormattedValue(index);
      input.setAttribute("type", "hidden");
      const row = this.getRow(index);
      setValue(this.rows[row], this.getField(index), this.cells[index].value);
    });
  }

  getDatatableConfig(): Object {
    return {
      height: (screen.height - 200) + "px"
    };
  }

  getGridStyles(): Object {
    let styles = {};
    if (this.height)
      styles["height"] = this.height;
    return styles;
  }

  getRowsStyles(): Object {
    return {
      display: "grid",
      height: "100%",
      "grid-template-columns": this.cols
    };
  }

  getHeaderClasses(index: number): string {
    let classes = "header";
    if (this.isRightAligned(index)) 
      classes += " " + this.RIGHT_ALIGNED;
    return classes;
  }

  getCellClasses(index: number): string {
    let classes = "cell";
    if (this.isSelected(index))
      classes += " " + this.SELECTED;
    if (this.isEvenLine(index)) 
      classes += " evenRow";
    if (this.isEditable(index)) 
      classes += " " + this.EDITABLE;
    if (this.isEditing(index))
      classes += " " + this.EDITING;
    else if (this.isRightAligned(index)) 
      classes += " " + this.RIGHT_ALIGNED;
    return classes;
  }

  getFormattedValue(index: number): string {
    return this.handler(index).formatter(this.cells[index].value);
  }

  private isRightAligned(index: number): boolean {
    return ["integer", "decimal", "hour", "date"].includes(this.inputType(index));
  }

  private isSelected(index: number): boolean {
    return this.cells[index].isSelected;
  }

  private inputType(index: number): string {
    return this.handler(index).type;
  }

  private handler(index: number): Handler {
    return this.columns.handlers[this.getColumn(index)];
  }

  private inputHandler(index: number): InputHandler {
    return this.handler(index) as InputHandler;
  }

  private nextEditableIndex(fromIndex: number): number {
    let next = fromIndex + 1;
    while (next < this.cells.length) {
      if (this.isEditable(next)) 
        return next;
      next++;
    }
    return -1;
  }

  private previousEditableIndex(fromIndex: number): number {
    let previous = fromIndex - 1;
    while (previous > -1) {
      if (this.isEditable(previous)) 
        return previous;
      previous--;
    }
    return -1;
  }

  private isEditable(index: number): boolean {
    return this.cells[index].isEditable;
  }

  private isEditing(index: number): boolean {
    return !!this.editings[index];
  }

  private setEditing(index: number, editing: boolean) {
    this.editings[index] = editing;
  }

  private getFirstVisibleRow(): number {
    return this.getRowOfScrollTop(this.gridRows.scrollTop);
  }

  private getRowOfScrollTop(scrollTop: number): number {
    return Math.floor(scrollTop / this.rowHeight);
  }

  private getRow(index: number): number {
    return Math.trunc(index / this.columnCount);
  }

  private getColumnsOfRow(rowIndex: number): number[] {
    return this.columns.fields.map((column) => { return this.columns.fields.indexOf(column) + (rowIndex * this.columnCount) });
  }

  private getColumn(index: number): number {
    return index % this.columnCount;
  }

  private getField(index: number): string {
    return this.columns.fields[index % this.columnCount];
  }

  private getOffsetTop(index: number): number {
    return this.getRow(index) * this.rowHeight;
  }

  private getOffsetLeft(index: number): number {
    let offset = 0;
    for (let col = 1; col <= this.getColumn(index); col++)
      offset += this.widths[col - 1];
    return offset;
  }
 
  private hasChanged(index: number): boolean {
    return this.input(index).value !== this.originalValues[index];
  }

  onScroll(): void {
    window.clearTimeout(this.isScrolling);
    this.isScrolling = setTimeout(() => {
      let firstRow = this.getFirstVisibleRow();
      let rowStop = firstRow;
      if (this.rowStartScroll < rowStop)
        for (let row = this.rowStartScroll; row < rowStop; row++)
          this.destroyRow(row);
      else {
        rowStop += this.visibleRowsCount;
        this.rowStartScroll += this.visibleRowsCount;
        for (let row = rowStop; row <= this.rowStartScroll; row++)
          this.destroyRow(row);
      }
      this.rowStartScroll = firstRow;
    }, 1000);
    this.loadRows();
  }

  loadRows(){
    let row = this.getFirstVisibleRow();
    if (row > 0)
      row -= 1;
    for (let offset = 0; offset < this.visibleRowsCount + 2; offset++) 
      this.createRow(row + offset);
  }

  private forIndexesOfRow(row: number, callback: (index: number) => void) {
    let offset = row * this.columnCount;
    for (let index = 0; index < this.columnCount; index++) 
      callback(index + offset);
  }

  private createRow(row: number): void {
    if (row >= 0 && row < this.rowsCount() && !this.isRowCreated[row]) {
      this.forIndexesOfRow(row, (index) => this.createCell(index));
      this.isRowCreated[row] = true;
    }
  }
  
  private createCell(index: number): void {
    let div = document.createElement("div");
    div.id = index + "-div";
    div["index"] = index;
    div.setAttribute("name", "cell");
  
    let span = document.createElement("span");
    div.appendChild(span);

    let input: HTMLInputElement = undefined;
    if (this.cells[index].isEditable) {
      input = document.createElement("input");
      div.appendChild(input);
    }

    this.configureCellElements(index, div, span, input);    
    this.rowsContainer.appendChild(div);
  }
  
  private destroyRow(row: number): void {
    if (row >= 0 && row < this.rowsCount() && this.isRowCreated[row]){
      this.forIndexesOfRow(row, this.destroyCell);
      this.isRowCreated[row] = false;
    }
  }
  
  private destroyCell(index: number): void {
    let div = document.getElementById(index + "-div");
    if (div)
      div.remove();  
  }
  
  private updateCellSizesAndPositions(): void {
    document.getElementsByName("cell").forEach(div => { 
      let index = div["index"];
      div.style["top"] = this.getOffsetTop(index) + "px";
      div.style["left"] = this.getOffsetLeft(index) + "px";
      div.style["width"] = this.widths[this.getColumn(index)] + "px";
    });
  }

  private configureCellElements(index: number, div: HTMLDivElement, span: HTMLSpanElement, input: HTMLInputElement): void {
    div.setAttribute("id", index + "-div");
    div.setAttribute("class", this.getCellClasses(index));  
    div.style["box-sizing"] = "border-box";
    div.style["position"] = "absolute";
    div.style["top"] = this.getOffsetTop(index) + "px";
    div.style["left"] = this.getOffsetLeft(index) + "px";
    div.style["height"] = this.rowHeight + "px";
    div.style["width"] = this.widths[this.getColumn(index)] + "px";

    if(this.cells[index].isClickable)
      div.addEventListener("click", () => this.onClickCodigosBarras(index));

    if (this.cells[index].isEditable)
      div.addEventListener("click", () => this.onClick(index));

    span.setAttribute("id", index + "-span");
    span.textContent = this.getFormattedValue(index);

    if (input) {
      input.setAttribute("id", index + "-input");
      input.setAttribute("type", "hidden");
      input.addEventListener("focusout", () => this.saveOrCancelEdit(index));
      input.addEventListener("keydown", (event: KeyboardEvent) => this.onKeyDown(index, event));
      if (this.isRightAligned(index))
        input.setAttribute("class", this.RIGHT_ALIGNED);
    }
  }  

  private setRowEditingClass(index: number) {
    this.forEachDivOfRow(this.getRow(index), div =>
      this.addClass(div, this.EDITING_ROW)
    );
  }

  private unsetRowEditingClass(index: number) {
    this.forEachDivOfRow(this.getRow(index), div =>
      this.removeClass(div, this.EDITING_ROW)
    );
  }

  private addClass(element: HTMLElement, clazz: string) {
    let classes = element.getAttribute("class");
    if (!classes.includes(clazz)) classes += " " + clazz;
    element.setAttribute("class", classes);
  }

  private removeClass(element: HTMLElement, clazz: string) {
    if (element) {
      let classes = element.getAttribute("class");
      while (classes.includes(clazz)) 
        classes = classes.replace(clazz, "");
      element.setAttribute("class", classes);
    }
  }

  private confirm(index: number): boolean {
    if (!this.validate(index))
      return false;
    if (this.confirmEdit) {
      const row = this.getRow(index);
      const confirmed = this.confirmEdit(
        row,
        this.rows[row],
        this.getField(index),
        this.input(index).value
      );
      if (confirmed)
        this.updateCellsOfRow(row);
      return confirmed;
    }
  }

  private validate(index: number): boolean {
    const htmlInput = this.input(index);
    return !!htmlInput ? this.inputHandler(index).validator(htmlInput.value) : true;
  }

  private updateCellsOfRow(row: number): void {
    let col = 0;
    this.forIndexesOfRow(row, (index) => {
      let field = this.columns.fields[col++];
      this.cells[index].value = getValue(this.rows[row], field);
      this.span(index).textContent = this.getFormattedValue(index);
    });
  }

  private forEachDivOfRow(
    row: number,
    callback: (div: HTMLDivElement) => void
  ) {
    let indexFirstColumn = row * this.columnCount;
    for (let offset = 0; offset < this.columnCount; offset++)
      callback(this.div(indexFirstColumn + offset));
  }

  private forCellElements(
    index: number,
    callback: (
      div: HTMLDivElement,
      span: HTMLSpanElement,
      input: HTMLInputElement
    ) => void
  ) {
    callback(this.div(index), this.span(index), this.input(index));
  }

  private div(index: number): HTMLDivElement {
    return document.getElementById(index + "-div") as HTMLDivElement;
  }

  private span(index: number): HTMLSpanElement {
    return document.getElementById(index + "-span") as HTMLSpanElement;
  }

  private input(index: number): HTMLInputElement {
    return document.getElementById(index + "-input") as HTMLInputElement;
  }

  private calculateRowHeight(): number {
    let span = document.createElement("span");
    span.textContent = "A";
    let div = document.createElement("div");
    div.appendChild(span);
    this.rowsContainer.appendChild(div);
    let offsetHeight = div.offsetHeight;
    div.remove();
    return offsetHeight + 6;
  }
}

export function LabelOnClick(): HandlerOnClick {
  return {
    type: "string",
    formatter: (value: string | number | Date): string => {
      return !!length ? value.toString().substring(0, length) : value.toString();      
    },
    handlerOnClick: (event: HandlerOnClick): boolean => {
      return true;
    }
  }
}

export function labelString(length?: number): Handler {
  return {
    type: "string",
    formatter: (value: string | number | Date): string => {
      return !!length ? value.toString().substring(0, length) : value.toString();      
    }
  }
}

export function inputString(length?: number): InputHandler {
  let label = labelString(length);
  return {
      type: label.type,
      handler: (event: KeyboardEvent): boolean => {
          return isAllowedEventKey(event) || !length || isFits(length, event);
      },
      formatter: label.formatter,
      validator: (value: string): boolean => {
        return true;
      },
      converter: (value: string): string => {
        return value;
      }
  }
}

export function labelInteger(length?: number, leadingZeros: boolean = false): Handler {
  return {
    type: "integer",
    formatter: (value: string | number | Date) => {
      return leadingZeros && !!length ? zeros(value.toString(), length) : value.toString();      
    }
  }
}

export function inputInteger(length?: number, leadingZeros: boolean = false): InputHandler {
  let label = labelInteger(length, leadingZeros);
  return {
      type: label.type,
      handler: (event: KeyboardEvent) => {
          return isAllowedEventKey(event) || (isNumber(event) && (!length || isFits(length, event)));
      },
      formatter: label.formatter,
      validator: isValidInteger,
      converter: (value: string): number => {
        return parseInt(value);
      }
  }
}

export function labelDecimal(decimals: number): Handler {
  return {
    type: "decimal",
    formatter: (value: string | number | Date) => {
      return (Math.round(parseFloat(value.toString()) * 100) / 100).toFixed(decimals).replace(".", ",");      
    }
  }
}

export function inputDecimal(length: number, decimals: number): InputHandler {
  let label = labelDecimal(decimals);
  return {
      type: label.type,
      handler: (event: KeyboardEvent) => {
        if (isAllowedEventKey(event))
          return true;

        if (!isDecimalEventKey(event))
          return false;

        let key = event.key === "." ? "," : event.key;

        let input = event.target as HTMLInputElement;
        let start = input.selectionStart;
        let end = input.selectionEnd;
        let value = input.value;

        if (key === "," && (value.substring(0, start) + value.substring(end)).includes(","))
          return false;

        value = value.substring(0, start) + key + value.substring(end);

        let integers = length - decimals;
        if (value.includes(",")) {
          let comma = value.indexOf(",");
          if (comma > integers || value.length - comma - 1 > decimals)
            return false;
        }
        else if (value.length > integers)
          return false;

        input.value = value;
        input.selectionStart = start + 1;
        input.selectionEnd = input.selectionStart;

        return false;
      },
      formatter: label.formatter,
      validator: isValidDecimal,
      converter: (value: string): number => {
        return parseFloat(value.replace(",", "."));
      }
  }
}

export function labelHour(): Handler {
  return {
    type: "hour",
    formatter: (value: string | number | Date) => {
      if (value instanceof Date) {
        let date = value as Date;
        let hour = zeros(date.getHours().toString(), 2);
        let minutes = zeros(date.getMinutes().toString(), 2);
        value = hour + ":" + minutes;
      }
      return value.toString();      
    }
  }
}

export function inputHour(): InputHandler {
  let label = labelHour();
  return {
      type: label.type,
      handler: (event: KeyboardEvent) => {
          if (isAllowedEventKey(event))
            return true;

          if (!isNumber(event))
            return false;

          let key = event.key;
          let input = event.target as HTMLInputElement;
          let start = input.selectionStart;
          let end = input.selectionEnd;
          let value = input.value;
  
          value = value.substring(0, start) + key + value.substring(end);

          if (!value.includes(":") && value.length > 2) {
            value = value.substring(0, 2) + ":" + value.substring(2);
            start++;
          }

          if (value.length >= 5) {
            let colon = value.indexOf(":");
            let hours = parseInt(value.substring(0, colon));
            let minutes = parseInt(value.substring(colon + 1));
            if (hours.toString().length > 2 || hours > 23 || minutes.toString().length > 2 || minutes > 59)
              return false;
          }
          
          input.value = value;
          input.selectionStart = start + 1;
          input.selectionEnd = input.selectionStart;

          return false;
      },
      formatter: label.formatter,
      validator: isValidHour,
      converter: (value: string): Date => {
        let hourMinutes = value.split(":");
        return new Date(0, 0, 0, parseInt(hourMinutes[0]), parseInt(hourMinutes[1]));
      }
  }
}

export function labelDate(): Handler {
  return {
    type: "date",
    formatter: (value: string | number | Date) => {
      if (value instanceof Date) {
        let date = value as Date;
        let day = zeros(date.getDate().toString(), 2);
        let month = zeros((date.getMonth() + 1).toString(), 2);
        let year = date.getFullYear().toString();
        value = day + "/" + month + "/" + year;
      }
      return value.toString();      
    }        
  }
}

export function inputDate(): InputHandler {
  let label = labelDate();
  return {
      type: label.type,
      handler: (event: KeyboardEvent) => {
        if (isAllowedEventKey(event))
          return true;

        if (!isNumber(event))
          return false;

        let key = event.key;
        let input = event.target as HTMLInputElement;
        let start = input.selectionStart;
        let end = input.selectionEnd;
        let value = input.value;

        value = value.substring(0, start) + key + value.substring(end);
        while (value.includes("/"))
          value = value.replace("/", "");

        if (value.length > 8)
          return false;
        else if (value.length > 2) {
          value = value.substring(0, 2) + "/" + value.substring(2);
          if (value.length > 5)
            value = value.substring(0, 5) + "/" + value.substring(5);
          start++;
        }

        if (value.length == 10 && !isValidDate(value))
          return false;

        input.value = value;
        input.selectionStart = start + 1;
        input.selectionEnd = input.selectionStart;

        return false;
      },
      formatter: label.formatter,
      validator: isValidDate,
      converter: (value: string): Date => {
        let dayMonthYear = value.split("/");
        return new Date(parseInt(dayMonthYear[2]), parseInt(dayMonthYear[1]) - 1, parseInt(dayMonthYear[0]));
      }
  }
}

function getValue(obj: any, property: string): any {
    let result = obj;
    property.split(".").forEach(prop => result = result[prop]);
    return result;
}

function setValue(obj: any, property: string, value: any): void {
    let target = obj;
    let properties = property.split(".");
    for (let index = 0; index < properties.length - 1; index++)
        target = target[properties[index]];
    target[properties[properties.length - 1]] =  value;
}

function isAllowedEventKey(event: KeyboardEvent): boolean {
  if (!event.key)
    return false;
  else if (event.ctrlKey && ["c", "x", "v"].includes(event.key.toLowerCase()))
      return true;
  return [ 
      "Home", "End", "Shift", "Alt", "Control", 
      "Backspace", "Delete", "ArrowLeft", "ArrowRight" 
  ].includes(event.key);
}

function isNumber(event: KeyboardEvent): boolean {
  return ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"].includes(event.key);
}

function isDecimalEventKey(event: KeyboardEvent): boolean {
  return isNumber(event) || [",", "."].includes(event.key);
}

function isFits(length: number, event: KeyboardEvent): boolean {
  let input = getInput(event);
  return input.value.length - (input.selectionEnd - input.selectionStart) < length;
}

function zeros(value: string, length?: number) {
  if (length)
    while (value.length < length)
      value = "0" + value;
  return value;
}

function getInput(event: KeyboardEvent): HTMLInputElement {
  if (event.target instanceof HTMLInputElement)
      return event.target as HTMLInputElement;   
  throw new Error("Parâmetro event não corresponde ao evento de elemento <input>");
}

// TODO Mover esses métodos para módulo de commons/utils/etc.
function isValidInteger(integer: string): boolean {
  return /^\d+$/.test(integer); 
}

function isValidDecimal(decimal: string): boolean {
  return /^\d+(,\d+)?$/.test(decimal);
}

function isValidHour(hour: string): boolean {
  return /^([10][0-9]|2[0-3]):([0-5][0-9])$/.test(hour);
}

function isValidDate(date: string): boolean {
  if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date))
    return false;

  let parts = date.split("/");
  let day = parseInt(parts[1], 10);
  let month = parseInt(parts[0], 10);
  let year = parseInt(parts[2], 10);

  if (year < 1000 || year > 3000 || month == 0 || month > 12)
    return false;

  let monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0))
    monthLength[1] = 29;

  return day > 0 && day <= monthLength[month - 1];
}